import React, { useState, useEffect } from 'react';
import { Bell, X, Users } from 'lucide-react';
import { useBroadcastInvites } from '../hooks/useBroadcastInvites';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

export default function BroadcastNotificationIcon() {
  const { invites, clearInvite } = useBroadcastInvites();
  const { addNotification } = useNotifications();
  const { token } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [lastInviteCount, setLastInviteCount] = useState(0);

  // Mostra notifica quando arriva un nuovo invito
  useEffect(() => {
    if (invites.length > lastInviteCount && lastInviteCount > 0) {
      const newInvites = invites.length - lastInviteCount;
      addNotification({
        message: `🔴 ${newInvites} nuovo${newInvites > 1 ? 'i' : ''} invito${newInvites > 1 ? 'i' : ''} broadcast!`,
        type: 'info'
      });
    }
    setLastInviteCount(invites.length);
  }, [invites.length, lastInviteCount, addNotification]);

  if (!invites.length) {
    return null; // Non mostrare l'icona se non ci sono inviti
  }

  const joinBroadcast = (streamToken, name) => {
    setShowDropdown(false);

    // Trigger evento per aprire il broadcast
    const event = new CustomEvent('openBroadcast', {
      detail: {
        name: `🔴 LIVE: ${name}`,
        url: `/api/broadcast/stream/${streamToken}?jwt=${token}`
      }
    });
    window.dispatchEvent(event);
  };

  const dismissInvite = (token) => {
    clearInvite(token);
  };

  return (
    <div className="relative">
      {/* Icona campana con badge */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative text-gray-300 hover:text-white p-1.5 transition-colors duration-200"
        title={`${invites.length} inviti broadcast`}
      >
        <Bell size={24} strokeWidth={2.2} />
        {/* Badge con numero inviti */}
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
          {invites.length}
        </span>
      </button>

      {/* Dropdown notifiche */}
      {showDropdown && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50">
          <div className="p-3 border-b border-gray-700">
            <h3 className="text-white font-medium flex items-center gap-2">
              <Users size={16} />
              Inviti Broadcast ({invites.length})
            </h3>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {invites.map((invite) => (
              <div key={invite.token} className="p-3 border-b border-gray-800 last:border-b-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      🔴 {invite.channelName}
                    </p>
                    <p className="text-gray-400 text-sm">
                      Sei stato invitato a un broadcast live
                    </p>
                  </div>
                  
                  <div className="flex gap-1">
                    {/* Pulsante Partecipa */}
                    <button
                      onClick={() => joinBroadcast(invite.token, invite.channelName)}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                      title="Partecipa al broadcast"
                    >
                      Partecipa
                    </button>
                    
                    {/* Pulsante Rifiuta */}
                    <button
                      onClick={() => dismissInvite(invite.token)}
                      className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                      title="Rifiuta invito"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Footer */}
          <div className="p-3 bg-gray-800/50 rounded-b-lg">
            <button
              onClick={() => {
                invites.forEach(invite => clearInvite(invite.token));
                setShowDropdown(false);
              }}
              className="w-full text-center text-gray-400 hover:text-white text-sm transition-colors"
            >
              Rimuovi tutti gli inviti
            </button>
          </div>
        </div>
      )}
      
      {/* Overlay per chiudere dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
}
